import { GetStaticProps, InferGetStaticPropsType } from "next";
import Head from "next/head";
import { ParsedUrlQuery } from "querystring";
import styled from "@emotion/styled";
import { colors } from "goi_common";

import { BRIDGE_CONTENTS, BridgeContent } from "@/constants/bridgeContent";

const SITE_ORIGIN = "https://heysangjo.co.kr";

interface Params extends ParsedUrlQuery {
  slug: string;
}

export const getStaticPaths = async () => {
  const paths = BRIDGE_CONTENTS.map((content) => ({
    params: { slug: content.slug },
  }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { slug } = params as Params;

  const content = BRIDGE_CONTENTS.find((c) => c.slug === slug) as BridgeContent;

  return {
    props: {
      content,
      // 기존 메타 파이프라인(_document/_app)이 소비하는 필드
      pageDescription: content.description,
      pageOgTitle: content.title,
      pageOgDescription: content.description,
      pageKeywords: "",
      pageOgImage: "",
    },
  };
};

const ContentBridgePage = ({ content }: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { slug, canonicalMode, canonicalUrl, title, bodyHtml } = content;

  const selfUrl = `${SITE_ORIGIN}/content/${slug}/`;
  // ★ 트릭 핵심: canonicalMode 에 따라 정본을 네이버(메인 가설) 또는 self(대조군)로 분기.
  //   canonical 을 self 로 "고치지" 말 것 - naver 로 가리키는 게 의도된 설계다.
  const canonicalHref = canonicalMode === "naver" ? canonicalUrl : selfUrl;

  return (
    <>
      <Head>
        {/* og:url 은 항상 자기 자신 (검증된 조합: og:url=self / canonical=naver) */}
        <meta property="og:url" content={selfUrl} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={canonicalHref} />
      </Head>

      <Article>
        <h1 className="subtitle_21_b">{title}</h1>
        <Body dangerouslySetInnerHTML={{ __html: bodyHtml }} />

        {/* 구글의 네이버 URL 발견 경로. 반드시 followable 해야 하므로 nofollow 붙는 NextLink 대신 일반 anchor 사용 */}
        {canonicalMode === "naver" && (
          <OriginLink href={canonicalUrl}>원문 보기</OriginLink>
        )}
      </Article>
    </>
  );
};

const Article = styled.article`
  padding: 24px 20px;
  color: ${colors.white};
`;

const Body = styled.div`
  margin-top: 20px;
  line-height: 1.7;

  h2 {
    margin: 28px 0 12px;
    font-size: 18px;
    font-weight: 700;
  }

  p {
    margin: 12px 0;
  }
`;

const OriginLink = styled.a`
  display: inline-block;
  margin-top: 32px;
  text-decoration: underline;
  color: ${colors.gray400};
`;

export default ContentBridgePage;
